import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import TriggerActionService from "./TriggerActionService";
import ActionTriggerConstants from "./ActionTriggerConstants";
import Localize from "./Localize/Localize";
import LocalizeKeys from "./Localize/LocalizeKeys";
import cookieManager from "./CookieManager";
import Constants from "./Constants";
import "./download-click-redirect.css";

// Query params that belong to a PREVIOUS AppsFlyer click, not to the new one.
// They must never be forwarded into a fresh OneLink click: AppsFlyer re-injects
// the incoming `referrer` into the destination URL without escaping it, so its
// inner "&" become real separators and every param — `pid` included — ends up
// duplicated in the landing URL we report as source_url.
const AF_CLICK_SCOPED_PARAMS = new Set([
    "referrer",
    "shortlink",
    "af_tranid",
    "af_xp",
    "af_click_lookback",
    "source_caller",
    "af_dp",
    "af_web_dp",
    "af_ios_url",
    "af_android_url",
]);

const DownloadClickRedirect = () => {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const deviceIdFromParam = searchParams.get("device_id");
        if (deviceIdFromParam) {
            cookieManager.remove(Constants.DEVICE_ID);
            cookieManager.set(Constants.DEVICE_ID, deviceIdFromParam);
        }

        const redirectUrl = searchParams.get("url");
        const originSearch = searchParams.get("origin_search");
        const deviceId = searchParams.get("device_id");
        const actionName =
            searchParams.get("actionName") ||
            ActionTriggerConstants.DOWNLOAD_LANDING_BUTTON_CLICK;

        // Resolve the Facebook click id (fbc) so it can be forwarded to AppsFlyer.
        // The Meta Pixel writes the _fbc cookie when the user lands with ?fbclid=...
        // If the cookie is not set yet, rebuild it from the fbclid param:
        // fbc = fb.1.<timestamp>.<fbclid>
        const resolveFbc = (params) => {
            const fbcCookie = cookieManager.get(Constants.META_FBC);
            if (fbcCookie) {
                return fbcCookie;
            }

            const fbclid = params.get("fbclid");
            if (fbclid) {
                return `fb.1.${Date.now()}.${fbclid}`;
            }

            return null;
        };

        const getFinalRedirectUrl = () => {
            if (!redirectUrl) {
                return null;
            }

            try {
                // Use the current origin as base so protocol-relative
                // (//host/path) and relative links resolve instead of throwing.
                const targetUrl = new URL(redirectUrl, window.location.origin);
                const originalParams = new URLSearchParams(originSearch || "");
                const isOneLink = targetUrl.hostname.endsWith("onelink.me");

                originalParams.forEach((value, key) => {
                    if (isOneLink && AF_CLICK_SCOPED_PARAMS.has(key)) {
                        return;
                    }
                    targetUrl.searchParams.set(key, value);
                });

                if (deviceId) {
                    targetUrl.searchParams.set("device_id", deviceId);
                }

                // Enrich AppsFlyer OneLinks with Meta identifiers so installs
                // can be traced back to the web user. Only touch onelink.me
                // targets to avoid polluting direct store/webgl links.
                if (isOneLink) {
                    const fbc = resolveFbc(originalParams);
                    const fbp = cookieManager.get(Constants.META_FBP);

                    if (fbc) {
                        targetUrl.searchParams.set("af_sub1", fbc);
                    }
                    if (fbp) {
                        targetUrl.searchParams.set("af_sub2", fbp);
                    }
                }

                return targetUrl.toString();
            } catch (e) {
                return redirectUrl;
            }
        };

        const finalRedirectUrl = getFinalRedirectUrl();

        const meta = {
            sourceUrl: "DownloadClickRedirect",
            buttonIndex: searchParams.get("buttonIndex"),
            buttonLink: finalRedirectUrl,
            originalButtonLink: redirectUrl,
            originSearch: originSearch || "",
            deviceId: deviceId || "",
            buttonActionName: actionName,
            buttonSmallTextKey: searchParams.get("buttonSmallTextKey") || "",
            buttonLargeTextKey: searchParams.get("buttonLargeTextKey") || "",
            buttonMediumTextKey: searchParams.get("buttonMediumTextKey") || "",
        };

        const redirect = () => {
            if (finalRedirectUrl) {
                window.location.replace(finalRedirectUrl);
            }
        };

        const sendAndRedirect = async () => {
            try {
                await Promise.allSettled([
                    TriggerActionService.sendTriggerAction(
                        ActionTriggerConstants.DOWNLOAD_LANDING_BUTTON_CLICK,
                        "",
                        meta
                    ),
                    TriggerActionService.sendTriggerAction(
                        actionName,
                        "",
                        meta
                    ),
                ]);
            } catch (e) {
                console.warn("[DownloadClickRedirect] trigger failed", e);
            } finally {
                redirect();
            }
        };

        sendAndRedirect();
    }, [searchParams]);

    return (
        <main className="download-redirect-page">
            <section className="download-redirect-card">
                <img
                    className="download-redirect-logo"
                    src="https://www.mundijuegoscash.com/wp-content/uploads/sites/38/2025/07/M_cash_logo.png"
                    alt="Mundijuegos Cash"
                />

                <div className="download-redirect-spinner" />

                <h1>
                    {Localize.translate(LocalizeKeys.DOWNLOAD_REDIRECT_TITLE)}
                </h1>

                <p>
                    {Localize.translate(LocalizeKeys.DOWNLOAD_REDIRECT_TEXT)}
                </p>
            </section>
        </main>
    );
};

export default DownloadClickRedirect;